import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useInstallPrompt } from "src/utils/install"

import "./InstallSettings.css"

/* The app on this device, in the settings pop-up (owner, 2026-10-17: *"they should be able to see
 * if they have the app installed or not. If they don't have the app installed, you have to sell
 * it"*). The row is always there and always says which it is.
 *
 * The banner is the interruption and it can be waved away for a month; this is the door that
 * stays open, which is why it reads `mode` rather than `promptMode`. Not installed, it carries the
 * pitch and the way this browser has: Chromium's real dialog behind "Install"; on iPhone, where
 * there is no button at all (owner: *"it's difficult to find the option ... We have to open share,
 * find the button"*), the three taps through Safari's share menu, unfolded by "How to install it"
 * in the player's language; inside another app's webview the same steps, headed by "open it in
 * Safari first"; on an Android browser that has offered no dialog, the browser's own menu. On a
 * computer, or Chrome on iOS, it says to open it on the phone. */
const SHARE_GLYPH = (
  <svg className="install-steps-glyph" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12" />
    <path d="m7 8 5-5 5 5" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </svg>
)

const GUIDES = {
  "manual-ios": { title: "install.iosTitle", intro: "install.iosIntro", steps: ["install.iosStep1", "install.iosStep2", "install.iosStep3"], glyph: true },
  "open-in-safari": { title: "install.iosTitle", intro: "install.iosSafariFirst", steps: ["install.iosStep1", "install.iosStep2", "install.iosStep3"], glyph: true },
  "manual-android": { title: "install.androidTitle", intro: "install.androidIntro", steps: ["install.androidStep1", "install.androidStep2", "install.androidStep3"], glyph: false },
}

const InstallSettings = () => {
  const { t } = useTranslation()
  const { mode, installed, promptInstall } = useInstallPrompt()
  const [open, setOpen] = useState(false)
  const guide = GUIDES[mode]

  return (
    <div className="install-settings">
      <div className="install-settings-head">
        <span className="section-label">
          {t("install.settingsTitle")}
          <span className={`install-settings-state${installed ? " is-installed" : ""}`}>
            {installed ? `✓ ${t("install.installedBadge")}` : t("install.notInstalled")}
          </span>
        </span>
        {!installed && mode === "prompt" ? (
          <button type="button" className="install-settings-button" onClick={() => promptInstall()}>
            {t("install.action")}
          </button>
        ) : null}
        {!installed && guide ? (
          <button type="button" className="install-settings-button" aria-expanded={open} onClick={() => setOpen((was) => !was)}>
            {t("install.howTo")}
          </button>
        ) : null}
      </div>
      <p className="p-mini install-settings-help">
        {installed ? t("install.installed") : mode === "none" ? t("install.elsewhere") : t("install.pitch")}
      </p>
      {!installed && guide && open ? (
        <div className="install-steps" role="region" aria-label={t(guide.title)}>
          <p className="p-mini install-steps-intro">
            <strong>{t(guide.title)}.</strong> {t(guide.intro)}
          </p>
          <ol className="install-steps-list">
            {guide.steps.map((step, index) => (
              <li key={step} className="install-steps-item">
                <span className="install-steps-number" aria-hidden="true">{index + 1}</span>
                <span className="p-mini install-steps-text">
                  {t(step)}
                  {guide.glyph && index === 0 ? <> {SHARE_GLYPH}</> : null}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  )
}

export default InstallSettings
